/**
 * reCAPTCHA v3 Service
 * Handles Google reCAPTCHA v3 integration for bot protection
 */

export interface RecaptchaConfig {
  siteKey: string;
  secretKey?: string; // Only used server-side
  minimumScore: number; // 0.0 to 1.0, higher is more human-like
}

export interface RecaptchaVerificationResult {
  success: boolean;
  score?: number;
  action?: string;
  challenge_ts?: string;
  hostname?: string;
  error?: string;
}

/**
 * reCAPTCHA Service for client-side integration
 */
export class RecaptchaService {
  private static instance: RecaptchaService;
  private config: RecaptchaConfig;
  private scriptLoaded: boolean = false;
  private loadingPromise: Promise<void> | null = null;

  private constructor(config: RecaptchaConfig) {
    this.config = config;
  }

  /**
   * Initialize the reCAPTCHA service
   */
  static initialize(config: RecaptchaConfig): RecaptchaService {
    if (!RecaptchaService.instance) {
      RecaptchaService.instance = new RecaptchaService(config);
    }
    return RecaptchaService.instance;
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): RecaptchaService {
    if (!RecaptchaService.instance) {
      throw new Error('RecaptchaService not initialized. Call initialize() first.');
    }
    return RecaptchaService.instance;
  }

  /**
   * Load the reCAPTCHA script
   */
  async loadScript(): Promise<void> {
    // If already loaded, return immediately
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    // If currently loading, return the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Create new loading promise
    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src*="recaptcha/api.js"]`
      );

      if (existingScript) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      // Create and load script
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.config.siteKey}`;
      script.async = true;
      script.defer = true;
      // Note: Google reCAPTCHA doesn't provide static SRI hashes as the script is dynamic
      // and doesn't require crossOrigin attribute for proper loading

      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };

      script.onerror = () => {
        this.loadingPromise = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  /**
   * Execute reCAPTCHA and get token
   */
  async executeRecaptcha(action: string = 'login'): Promise<string> {
    try {
      // Ensure script is loaded
      await this.loadScript();

      // Wait for grecaptcha to be ready
      await this.waitForRecaptcha();

      // Execute reCAPTCHA
      return new Promise((resolve, reject) => {
        (window as any).grecaptcha.ready(() => {
          (window as any).grecaptcha
            .execute(this.config.siteKey, { action })
            .then((token: string) => {
              resolve(token);
            })
            .catch((error: Error) => {
              reject(error);
            });
        });
      });
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      throw new Error('Failed to execute reCAPTCHA');
    }
  }

  /**
   * Wait for grecaptcha to be available
   */
  private waitForRecaptcha(timeout: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkRecaptcha = () => {
        if ((window as any).grecaptcha && (window as any).grecaptcha.ready) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('reCAPTCHA loading timeout'));
        } else {
          setTimeout(checkRecaptcha, 100);
        }
      };

      checkRecaptcha();
    });
  }

  /**
   * Verify reCAPTCHA token on the server
   * Note: This should be called from your backend, not directly from the client
   */
  static async verifyToken(
    token: string,
    secretKey: string,
    remoteIp?: string
  ): Promise<RecaptchaVerificationResult> {
    try {
      const params = new URLSearchParams({
        secret: secretKey,
        response: token,
        ...(remoteIp && { remoteip: remoteIp }),
      });

      const response = await fetch(
        'https://www.google.com/recaptcha/api/siteverify',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        }
      );

      const result = await response.json();

      return {
        success: result.success,
        score: result.score,
        action: result.action,
        challenge_ts: result.challenge_ts,
        hostname: result.hostname,
        error: result['error-codes']?.[0],
      };
    } catch (error) {
      console.error('reCAPTCHA verification failed:', error);
      return {
        success: false,
        error: 'Verification request failed',
      };
    }
  }

  /**
   * Check if a score meets the minimum threshold
   */
  static isScoreValid(score: number, minimumScore: number = 0.5): boolean {
    return score >= minimumScore;
  }

  /**
   * Get the site key
   */
  getSiteKey(): string {
    return this.config.siteKey;
  }

  /**
   * Get the minimum score threshold
   */
  getMinimumScore(): number {
    return this.config.minimumScore;
  }
}

/**
 * Hook for using reCAPTCHA in React components
 */
export function useRecaptcha() {
  const executeRecaptcha = async (action: string = 'login'): Promise<string> => {
    try {
      const service = RecaptchaService.getInstance();
      return await service.executeRecaptcha(action);
    } catch (error) {
      console.error('Failed to execute reCAPTCHA:', error);
      throw error;
    }
  };

  return { executeRecaptcha };
}

export default RecaptchaService;